import qs from "qs";

import {
  AuthenticateMethod,
  type AuthenticateResultErrorType,
  type DataTypeStatus,
  type webAgent__EndpointType,
} from "@medbillai/generated-graphql-types";

import type { CaseType } from "@medbillai/schemas";

export type LoginParams = {
  forceLogout?: boolean;
  authError?: AuthenticateResultErrorType;
  redirect?: string;
  /**
   * Create-account framing (+ web hero) on the email-entry screen. Not
   * preserved through the magic-link round trip.
   */
  variant?: "signup";
};

export type PreviewParams = {
  title: string;
  uri: string;
};

export type HipaaReleaseParams = {
  templateId?: string;
  eventId?: string;
  issueId?: string;
};

export type HipaaViewReleaseParams =
  | { templateReleaseId: string; templateId?: never }
  | { templateId: string; templateReleaseId?: never };

export type HipaaErrorParams = {
  viewPdfError?: boolean;
};

export interface CreateAuthRedirectUriOptions {
  baseUri: string;
  platform: "ios" | "android" | "web";
  method: AuthenticateMethod;
  applinkSchemeUri?: string;
}

export type FeedbackParams = {
  issueId: string;
  rating?: number;
  feedbackId?: string;
};

export type FeedbackDeepLinkParams = FeedbackParams & {
  showFeedback?: boolean;
};

export type AuthenticateRedirectUrlParams = {
  token: string;
  platform: string;
};

export type StytchAuthenticationParams = AuthenticateRedirectUrlParams & {
  stytch_token_type: string;
};

export type PreviewCaseMessageInfo =
  | {
      type: "USER";
      message: string;
    }
  | {
      type: "SYSTEM";
      messageId: string;
      message: string;
      options?: string[];
    };

export const CASE_PREVIEW_ID = "preview";

/**
 * Appends query parameters to a URL, handling both URLs with and without existing query strings.
 * If the URL already contains query parameters (has a '?'), new params are appended with '&'.
 * Otherwise, new params are appended with '?'.
 */
export const buildUrlWithParams = (
  url: string,
  params?: Record<string, unknown>,
): string => {
  const filteredParams = Object.fromEntries(
    Object.entries(params || {}).filter(([_, value]) => value !== undefined),
  );
  if (Object.keys(filteredParams).length > 0) {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${qs.stringify(filteredParams)}`;
  }
  return url;
};

// TODO - look into generating these

// Onboarding & auth
export const welcomeUrl = (): string => "/welcome";
export const getStartedUrl = (params?: {
  /** Suppresses the intake-routing questions for arrivals that already
   *  answered them (e.g. the Natera funnel's not-Natera exit). */
  skipIntakeRouting?: boolean;
}): string => buildUrlWithParams("/get-started", params);
export const onboardingUrl = (): string => "/onboarding";
export const activateUrl = (params?: { inviteToken?: string }): string =>
  buildUrlWithParams("/activate", params);
export const nateraIntakeUrl = (): string => "/natera-intake";
export const nateraIntakeActivateUrl = (): string => "/natera-intake/activate";
export const internalOnboardingFlowV3Url = (
  slug: string,
  options?: { intent?: string; skipSignUp?: boolean; firstName?: string },
): string => {
  const params = new URLSearchParams();
  if (options?.intent) {
    params.set("intent", options.intent);
  }
  if (options?.firstName) {
    params.set("firstName", options.firstName);
  }
  if (options?.skipSignUp) {
    params.set("skipSignUp", "true");
  }
  const query = params.toString();
  return `/internal/onboarding-flow-v3/${slug}${query ? `?${query}` : ""}`;
};
export const internalOnboardingFlowFfgaUrl = (
  slug: string,
  options: {
    intent?: string;
    skipSignUp?: boolean;
    firstName?: string;
    faOutcome: string;
  },
): string => {
  const params = new URLSearchParams();
  if (options.intent) {
    params.set("intent", options.intent);
  }
  if (options.firstName) {
    params.set("firstName", options.firstName);
  }
  if (options.skipSignUp) {
    params.set("skipSignUp", "true");
  }
  params.set("faOutcome", options.faOutcome);
  const query = params.toString();
  return `/internal/onboarding-flow-ffga/${slug}${query ? `?${query}` : ""}`;
};
export const loginUrl = (params?: LoginParams): string => {
  if (!params) {
    return "/login";
  }
  return `/login?${qs.stringify(params)}`;
};
export const VERIFY_EMAIL_CROSS_BROWSER_LINK = "cross-browser";

export const verifyEmailUrl = (
  email: string,
  { crossBrowserLink = false }: { crossBrowserLink?: boolean } = {},
): string => {
  if (!email) {
    return "/login";
  }
  // Carried in the URL so the screen shows what was sent, even after a reload
  // or a flag change since the send.
  const link = crossBrowserLink
    ? `&link=${VERIFY_EMAIL_CROSS_BROWSER_LINK}`
    : "";
  return `/verify-email?email=${encodeURIComponent(email)}${link}`;
};
/** Pathname for the auth callback screen (no query). */
export const authenticatePathname = (): string => "/authenticate";

// Used for magic links with the paste url button.
export const authenticateUrl = (params: StytchAuthenticationParams): string =>
  buildUrlWithParams(authenticatePathname(), params);

export const createAuthRedirectUri = ({
  baseUri,
  applinkSchemeUri,
  platform,
  method,
}: CreateAuthRedirectUriOptions): string => {
  // In this case we want to ensure the redirect is always navigated back into
  // the app. Google oauth if already authenticated will stay in the iframe, so
  // we use the applinkSchemeUri to navigate back into the app.
  // If the applinkSchemeUri is not set, we default to the baseUri.
  if (
    method === AuthenticateMethod.OAUTH &&
    (platform === "ios" || platform === "android") &&
    applinkSchemeUri
  ) {
    return `${applinkSchemeUri}authenticate?platform=${platform}`;
  }
  const url = new URL(authenticatePathname(), baseUri);
  url.searchParams.set("platform", platform);
  return url.toString();
};

/**
 * PaymentSheet 3DS return into the native app. Same join as
 * `createAuthRedirectUri` + `applinkSchemeUri` (`mbai://` in app.config).
 */
export const stripeNativeReturnUrl = (
  applinkSchemeUri: string = "mbai://",
): string => {
  const uri = applinkSchemeUri.includes("://")
    ? applinkSchemeUri
    : `${applinkSchemeUri}://`;
  return `${uri}stripe-redirect`;
};

export const stripeWebReturnUrl = (baseUrl: string): string => {
  const uri = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return `${uri}stripe-redirect`;
};

// Home Tabs
export const homeUrl = (): string => "/";
// Should only be used when navigating inside the tab router
// TODO: Because of the limitation of using headerLarge needing a stack router
// we cannot have a default initial route for the tab router, we need to update
// all routing to use /home but in the mean time because it is the default route
// we can use this till we update all routing, this will effect the web app
// minorly but it is a small fix
export const homeTabsUrl = (): string => "/home";
export const accountsUrl = (): string => "/accounts";
export const issuesUrl = (): string => `/cases`;
export const billsUrl = (): string => `/bills`;
export const coverageUrl = (): string => "/coverage";
export const coverageWhereYouStandUrl = (): string =>
  "/coverage/where-you-stand";
export const coverageWhatYouPayUrl = (): string => "/coverage/what-you-pay";
export const coveragePlanDetailsUrl = (): string => "/coverage/plan-details";
export const coverageCardUrl = (): string => "/coverage/card";
/** The card full screen, to show at a front desk. */
export const coverageCardFullUrl = (): string => "/coverage/card-full";
export const coverageServiceUrl = (category: string): string =>
  `/coverage/what-you-pay/${category}`;
/** `start: "profile"` reopens the name and date of birth step first. */
export const coverageSetupUrl = (params?: { start?: "profile" }): string =>
  buildUrlWithParams("/coverage-setup", params);
export const flaggedBillsUrl = (): string => `/bills/flagged`;

// Flow
export const documentUrl = (documentId: string): string =>
  `/document/${documentId}`;
export const documentPreviewUrl = () => "/document/preview";

export const issueUrl = ({
  issueId,
  unread = false,
  skipPageUILoad,
  title,
  open,
}: {
  issueId: string;
  unread?: boolean;
  skipPageUILoad?: boolean;
  title?: string | null;
  /** An event whose flow the case screen opens on arrival. */
  open?: string;
}): string => {
  const baseUrl = `/case/${issueId}`;
  const params = {
    unread: unread.toString(),
    ...(skipPageUILoad && { skipPageUILoad: skipPageUILoad.toString() }),
    ...(title && { title }),
    ...(open && { open }),
  };
  return buildUrlWithParams(baseUrl, params);
};
export const issuePreviewUrl = (params: {
  title?: string;
  caseType?: CaseType;
  storeKey?: string;
  config?: string;
}) => {
  return buildUrlWithParams(`/case/${CASE_PREVIEW_ID}`, params);
};

// Bills
export const billUrl = (
  eobId: string,
  billName: string,
  decision: string,
  kind?: "claim" | "invoice" | "statement" | "collectionsNotice",
): string => {
  const base = `/bill/${encodeURIComponent(eobId)}?billName=${encodeURIComponent(billName)}&decision=${encodeURIComponent(decision)}`;
  return kind && kind !== "claim" ? `${base}&kind=${kind}` : base;
};
export const billDisputeUrl = (eobId: string): string => {
  return `/bill-dispute?id=${eobId}`;
};

// Configuration / Onboarding
export const linkInsuranceUrl = (): string => "/link-insurance";
export const allowNotificationsUrl = (): string => "/allow-notifications";
export const onboardingCompletedUrl = (): string => "/completed";

// Hipaa Release
export const hipaaReleaseUrl = (params?: HipaaReleaseParams): string =>
  `/hipaa-release?${qs.stringify(params)}`;
export const hipaaAgreementUrl = (params?: HipaaReleaseParams): string =>
  `/hipaa-agreement?${qs.stringify(params)}`;
export const hipaaViewReleaseUrl = (params?: HipaaViewReleaseParams): string =>
  `/hipaa-release-preview?${qs.stringify(params)}`;
export const hipaaReleaseErrorUrl = (params?: HipaaErrorParams): string =>
  `/hipaa-release/error?${qs.stringify(params)}`;

// Case Intake

// This should most likely never be used directly.
// When navigating to a case intake flow, use the useSetupCaseFlow hook
export const caseIntakeUrl = (): string => {
  return `/case-intake`;
};

export const caseIntakeFlowUrl = (step: string): string => {
  return `${caseIntakeUrl()}/${step}`;
};
export const caseIntakeFlowErrorUrl = (): string => "/case-intake/error";
export const caseDetailsUrl = (caseId: string): string => {
  return `/case/details/${caseId}`;
};
// Settings
export const settingsUrl = (): string => "/settings";
export const settingPreviewUrl = (params: PreviewParams): string => {
  return `/settings/preview?${qs.stringify(params)}`;
};
export const settingsReleaseListUrl = (): string => "/settings/release-list";
export const settingsAccountSettingsUrl = (): string => "/settings/account";
export const settingsShadowLoginUrl = (): string => "/settings/shadow-login";
export const settingsDeleteAccountUrl = (): string =>
  "/settings/delete-account";

export const settingsReceiptsUrl = (): string => "/settings/receipts";
export const settingsLinkedAccountsUrl = (): string =>
  "/settings/linked-accounts";

// Insurance
// Accounts Link
export type InsuranceLinkType = webAgent__EndpointType | "all";
export type InsuranceLinkFrom =
  | {
      placeFrom: "insuranceHome";
      linkType?: InsuranceLinkType;
    }
  | {
      placeFrom: "chat";
      chatId: string;
      linkType?: InsuranceLinkType;
    };

// Accounts Link - Base
export const accountsLinkUrl = (): string => "/accounts-link";
export const accountsLinkSelectUrl = (from?: InsuranceLinkFrom): string =>
  buildUrlWithParams("/accounts-link/select", from);
export const accountsLinkErrorUrl = (): string => "/accounts-link/error";
export const accountsLinkUnsupportedPlanUrl = (): string =>
  "/accounts-link/unsupported-plan";

export const accountsLinkNotificationsUrl = (): string =>
  "/accounts-link/accounts-notifications";

// Accounts Link - Web Agent
export const accountsLinkWebAgentLinkUrl = (): string => "/accounts-link/link";

export type AccountsLinkWebAgentRelinkUrlParams = Record<string, never>;
export const accountsLinkWebAgentRelinkUrl = ({
  linkId,
  params,
}: {
  linkId: string;
  params?: AccountsLinkWebAgentRelinkUrlParams;
}): string => {
  return buildUrlWithParams(`/accounts-link/link/relink/${linkId}`, params);
};

export const accountsLinkWebAgentMfaSelectUrl = (): string =>
  "/accounts-link/link/mfa-select";
export const accountsLinkWebAgentMfaCodeUrl = (): string =>
  "/accounts-link/link/mfa-code";
export const accountsLinkWebAgentSecurityQuestionUrl = (): string =>
  "/accounts-link/link/security-question";
export const accountsLinkWebAgentSuccessUrl = (params: {
  hasBenefitsSummary: boolean;
}): string => {
  const baseUrl = "/accounts-link/link/success";
  return `${baseUrl}?hasBenefitsSummary=${params.hasBenefitsSummary.toString()}`;
};
export const accountsLinkWebAgentNotificationsUrl = (): string =>
  "/accounts-link/link/accounts-notifications";
export const accountsLinkWebAgentAlternateInsuranceUrl = (): string =>
  "/accounts-link/link/alternate-account";
export const accountsLinkWebAgentPlanDocumentsLoadingUrl = (): string =>
  "/accounts-link/link/plan-documents-loading";
export function accountsLinkWebAgentPlanDocumentsLoadedUrl(
  status: DataTypeStatus.SUCCESS,
  caseId: string,
): string;
export function accountsLinkWebAgentPlanDocumentsLoadedUrl(
  status: Exclude<DataTypeStatus, DataTypeStatus.SUCCESS>,
): string;
export function accountsLinkWebAgentPlanDocumentsLoadedUrl(
  status: DataTypeStatus,
  caseId?: string,
): string {
  return buildUrlWithParams("/accounts-link/link/plan-documents-loaded", {
    status,
    caseId,
  });
}

// Note: feedback used to be its own route, but this now goes directly to the
// case screen with feedback params. The old route still exists, and redirects
// to the case screen, but this function goes directly to the case screen to
// save future deeplinks the bounce.
export const feedbackDeepLinkUrl = (params: FeedbackDeepLinkParams): string => {
  const caseUrl = issueUrl({ issueId: params.issueId, unread: true });
  return buildUrlWithParams(caseUrl, {
    showFeedback: params.showFeedback ?? "true",
    rating: params.rating?.toString(),
    feedbackId: params.feedbackId,
  });
};

// Person
export const peopleUrl = (): string => "/people";
export const peopleCreateUrl = (): string => "/people/create";
export const peopleEditUrl = (personId: string): string =>
  `/people/edit?id=${personId}`;

// Payment
export const paymentPreauthorizeUrl = (
  eventId?: string,
  issueId?: string,
): string => buildUrlWithParams("/payment/preauthorize", { eventId, issueId });
export const paymentSuccessUrl = (): string => "/payment/success";
export const paymentAgreementUrl = (
  eventId: string,
  issueId?: string,
): string =>
  buildUrlWithParams("/payment/payment-agreement", { eventId, issueId });

// Admin
export const shadowLoginUrl = (params?: {
  userId?: string;
  next?: string;
}): string => buildUrlWithParams("/shadow-login", params);

// Mobile only
export const previewUrl = (params: PreviewParams): string => {
  return `/preview?${qs.stringify(params)}`;
};

/** Returns true when the path belongs to the cases list or an individual case. */
export const isCaseRoute = (path: string): boolean =>
  path === "/cases" || path.startsWith("/case/");

// Admin URLs (GrantCentral internal tools)
export const adminCaseUrl = (caseId: string): string =>
  `/ha/case?issueId=${caseId}`;

export const adminUserUrl = (userId: string): string =>
  `/ha/manage-user?userId=${userId}`;

/** The Integrations app: same GrantCentral host as `/ha`, `/integrations` prefix. */
export const adminIntegrationsOpsUrl = (sessionId: string): string =>
  buildUrlWithParams("/integrations/ops", { sessionId });

export const grantedLegalTermsUrl = (): string =>
  "https://grantedhealth.com/legal/terms";

export const grantedSecurityUrl = (): string =>
  "https://grantedhealth.com/security";
